import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Divider,
  FormControl,
  Typography,
  IconButton,
  FormHelperText,
  Button,
} from "@mui/material";
import Grid from "@mui/system/Grid";
import { isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Controller, useForm } from "react-hook-form";
import { nanoid } from 'nanoid';

import { ListOptions } from "../../../models/Master";
import {
  flexAllCentre,
  flexAllEnd,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import {
  AddUserModalGrid,
  AutocompleteDropDownMaxWidth,
  ContentScrollable,
} from "../../HeaderMenu/style/style";
import {
  ButtonCloseStyle,
  ButtonVerifyStyle,
  FaXmarkIcon,
  HorizontalDivider,
} from "../../../app/styles/styles";
import { ButtonContained } from "../../../app/common/button";
import colors from "../../../styles/colors/colors";
import { rules } from "../../../utils/validation/Validation";
import {
  DropDownPadding,
  RiDeleteIcon,
} from "../../../components/styles/styles";
import { MyAccountModel } from "../../../models/MyAccount";
import { FormInputDropdown } from "../../../components/FormInputDropdown";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";
import fontWeight from "../../../styles/mui/fontWeight";
import { Asterisk } from "../../../styles/common/style";
import {
  ButtonErrorWidth,
  ClearErrorButtonBox,
  DocGridBody,
  documentMargin,
  DocumentTypeIcon,
} from "../../PriorAuthWorkFlow/styles/style";
import FileUpload from "../../../components/FormPriorAuthReAuth/FileUpload";
import { getDocTypeIcon } from "../../../utils/DocIcons";
import {
  appendTimestampToFilename,
  currentDate,
  extractDocType,
  getFormattedDateTime,
  getValue,
} from "../../../utils";
import { maxFileSize } from "../../../constants/Config";
import { allowedTypesForNOMNC } from "../../../constants/Patient";
import {
  DocumentError2,
  DocumentError3,
  DocumentError5,
  DocumentError6,
  DocumentError7,
} from "../../../constants/FunctionalUtils";
import { FlexColumn } from "../../CreatePriorAuth/style/style";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import {
  NoMncDetailsActionDispatchTypes,
  ReferralDocumentFileDispatchTypes,
} from "../../../constants/Referral";
import { NOMNC_Modal } from "../style/style";

export interface PropsFromState {
  setNOMNCModalflag: (val: boolean) => void;
  loggedInUser: MyAccountModel;
  getDischargeReason: () => void;
  dischargeReason: ListOptions[];
  putUploadNoMNCDocumentDetails: (payload: UploadNoMNCDocumentDetails) => void;
  noMncDetails: any;
  uploadDocumentFile: (payload: any) => void;
  resetStates: (actionType: string, field?: string) => void;
  referralId: number;
  nOMNModalFlag: boolean;
  documentUploadState: any;
  documentUploadErrorState: any;
}

export interface UploadNoMNCDocumentDetails {
  referralId: number;
  dischargeDate: string;
  noMNCIssuedDate: string;
  dischargeReasonId: number;
  dischargeVerified: boolean;
  noMncReceived: boolean;
}

const NoMncModal: React.FC<PropsFromState> = ({
  referralId,
  nOMNModalFlag,
  setNOMNCModalflag,
  putUploadNoMNCDocumentDetails,
  getDischargeReason,
  dischargeReason,
  uploadDocumentFile,
  documentUploadState,
  loggedInUser,
  resetStates,
}) => {
  const methods = useForm<any>({
    defaultValues: "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<any>(null);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [fileUpload, setFileUpload] = useState<File[]>([]);

  const maxFileSizeInBytes = Number(maxFileSize) * 1000000;
  const { handleSubmit, getValues, setValue, control } = methods;

  const onSubmit = () => {
    const {email, firstName, lastName} = getValue(loggedInUser, "providerPortalUser");
    const requestId = uuidv4();
    if (fileUpload && fileUpload.length > 0) {
      const formData = new FormData();
      formData.append("ReferralId", referralId.toString());
      formData.append("RequestId", requestId);
      formData.append("UserCreated", email);
      formData.append("DocumentData.ReferralBlobId", "0");
      formData.append("DocumentData.DocumentId", "15");
      formData.append("DocumentData.BlobId", "2");
      formData.append(
        "DocumentData.DateTimeReceived",
        formatDate(currentDate(true), DateFormats.MM_DD_YYYY),
      );
      formData.append("DocumentData.DocumentSourceId", "1");
      formData.append(
        "DocumentData.UserFullName",
        `${firstName} ${lastName} `,
      );
      formData.append("ReferralDocumentDetail.Type", "1");
      formData.append("DocumentData.ReferralDocument", "");
      fileUpload.forEach((file: File, index: number) => {
        const updatedFile = new File(
          [file],
          appendTimestampToFilename(file.name, getFormattedDateTime(index + 1)),
          { type: file.type },
        );
        formData.append("referralDocumentList", updatedFile);
      });
      formData.append(
        "ReferralDocumentDetail.DocumentationDate",
        formatDate(currentDate(true), DateFormats.MM_DD_YYYY),
      );
      formData.append("ReferralDocumentDetail.ContainsSupportedDoc", "true");
      formData.append("ReferralDocumentDetail.ServiceRequestId", "0");// ServiceRequestId is sent as 0, as it is not required for NOMNC upload as discussed with BE team.
      formData.append("DocumentData.IsPortalDocument", "true");
      uploadDocumentFile(formData);
    }
  };

  const submit = handleSubmit(() => {
    onSubmit();
  });

  useEffect(() => {
    getDischargeReason();
    return () => {
      setFileUpload([]);
      resetStates(
        NoMncDetailsActionDispatchTypes.NO_MNC_DOCUMENT_DETAILS_RESET,
      );
      resetStates(ReferralDocumentFileDispatchTypes.REFERRAL_DOCUMENT_RESET);
    };
  }, []);

  useEffect(() => {
    if (documentUploadState && documentUploadState.referralId > 0) {
      setNOMNCModalflag(false);
      if (referralId > 0) {
        putUploadNoMNCDocumentDetails({
          referralId: referralId,
          dischargeDate: getValues().dischargeDate,
          noMNCIssuedDate: formatDate(
            getValues().nomncIssuedDate,
            DateFormats.YYYY_MM_DD,
          ),
          dischargeReasonId: getValues().dischargeReason === "" ? null : getValues().dischargeReason,
          dischargeVerified: true,
          noMncReceived: true,
        });
      }
    }
  }, [documentUploadState]);

  useEffect(() => {
    if (files && files.length > 0) {
      const selectedFiles = files;
      if (!selectedFiles) return;
      let newTotalSize = totalSize;
      const newFiles: any[] = [];
      let errorMessage = null;

      Array.from(selectedFiles).forEach((file) => {
        const typedFile = file;
        if (!allowedTypesForNOMNC.includes(typedFile.type)) {
          errorMessage = `${DocumentError2} ${typedFile.name}`;
          return;
        }
        if (typedFile.size === 0) {
          errorMessage = `${typedFile.name} - ${DocumentError5}`;
          return;
        }
        newTotalSize += typedFile.size;
        if (newTotalSize > maxFileSize) {
          errorMessage = DocumentError3;
          return;
        }
         if (newFiles && newFiles.length > 1) {
           errorMessage = `${DocumentError7}`;
         }
        newFiles.push(typedFile);
      });

      if (fileUpload && fileUpload.length === 1) {
        errorMessage = `${DocumentError6}`;
      }

      if (errorMessage) {
        setError(errorMessage);
        setFiles([]);
      } else {
        setFileUpload([...fileUpload, ...newFiles]);
        setFiles([]);
        setTotalSize(newTotalSize);
        setError(null);
      }
    }
  }, [files]);

  useEffect(() => {
    setValue(
      "documentUploads",
      getValue(fileUpload, "[0].name")
        ? fileUpload.map((file: File) => {
            const entries = Object.entries(file).filter(
              ([key]) => key !== "lastModifiedDate",
            );
            return Object.fromEntries(entries);
          })
        : [],
    );
  }, [fileUpload]);

  const handleDelete = (fileName: string, fileSize: number) => {
    setFileUpload(fileUpload.filter((file: File) => file.name !== fileName));

    setError(null);
    const newTotalSize = Math.max(0, totalSize - fileSize);
    setTotalSize(newTotalSize);
  };

  return (
    <>
      <Modal
        open={nOMNModalFlag}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ ...flexAllCentre, zIndex: 1100 }}
      >
        <Box sx={NOMNC_Modal}>
          <Grid container mb="0.4rem">
            <Grid size={{ xs: 11.5 }}>
              <Typography id="modal-modal-title" variant="h1">
                Upload NOMNC
              </Typography>
            </Grid>
            <Grid size={{ xs: 0.5 }}>
              <IconButton onClick={() => setNOMNCModalflag(false)}>
                <FaXmarkIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Box>
            <Typography variant="h5" p={2} sx={{ color: colors.primary.main }}>
              Please enter the discharge date and upload a completed health plan
              specific NOMNC.
            </Typography>
          </Box>
          <Box mb="2rem" sx={ContentScrollable}>
            <Grid container>
              <Grid size={{ xs: 4 }} mb={1} sx={DropDownPadding}>
                <Box mb={2} pr={2} sx={flexColumn}>
                  <FormControl variant="standard" sx={{ mb: 2 }}>
                    <DatePicker
                      name="dischargeDate"
                      control={control}
                      label={"Discharge Date:"}
                      helper={rules.dischargeDate}
                      manualEntryNotAllowed={true}
                    />
                  </FormControl>
                  <FormControl variant="standard">
                    <DatePicker
                      name="nomncIssuedDate"
                      control={control}
                      label={"NOMNC Issued Date:"}
                      helper={rules.nomncIssuedDate}
                      manualEntryNotAllowed={true}
                    />
                  </FormControl>
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }} mb={1} sx={DropDownPadding}>
                <Typography
                  variant="body1"
                  fontWeight={fontWeight[3]}
                  color={colors.fonts[2]}
                  lineHeight={"0.95rem"}
                >
                  Discharge Reason :
                </Typography>
                <FormControl
                  variant="standard"
                  fullWidth
                  sx={AutocompleteDropDownMaxWidth}
                >
                  <FormInputDropdown
                    name="dischargeReason"
                    control={control}
                    list={dischargeReason}
                    disabled={false}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box>
            <Grid container sx={FlexColumn}>
              <Grid container sx={flexAllCentre}>
                <Grid size={{ xs: 12 }} pl={2} sx={flexRow}>
                  <Typography
                    fontWeight={fontWeight[3]}
                    color={colors.fonts[2]}
                  >
                    Document upload is mandatory
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color={colors.red[103]}
                    fontWeight={fontWeight[2]}
                    pr={2}
                    sx={Asterisk}
                  >
                    *
                  </Typography>
                </Grid>
              </Grid>
              <Grid container pb={"0.1rem"} sx={flexAllCentre}>
                <Grid size={{ xs: 12 }} sx={DocGridBody}>
                  <Controller
                    name={"documentUploads.0.path"}
                    control={control}
                    defaultValue={""}
                    rules={rules.document}
                    render={({ fieldState: { error } }) => {
                      return (
                        <>
                          <FileUpload
                            files={files}
                            setFiles={setFiles}
                            maxFileSizeInBytes={maxFileSizeInBytes}
                            minFileSize={1}
                            type={1}
                            disabled={files && files.length === 1}
                            isVisible={false}
                            multipleFilesAllowed={false}
                          />
                          {error && fileUpload.length === 0 && (
                            <FormHelperText>{error.message}</FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container pb={"1rem"} sx={flexAllCentre}>
                <Grid size={{ xs: 8 }} pl={2} sx={documentMargin}>
                  {fileUpload &&
                    fileUpload.map((data: any, index: number) => (
                      <>
                        <Grid container key={nanoid()} sx={flexRow} pt={"0.2rem"}>
                          <Grid size={{ xs: 10 }} sx={flexRow}>
                            <Box sx={DocumentTypeIcon}>
                              {getDocTypeIcon(extractDocType(data.type))}
                            </Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={fontWeight[4]}
                              color={colors.fonts[4]}
                              pt={1}
                            >
                              {data.name}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 2 }}>
                            <RiDeleteIcon
                              onClick={() => handleDelete(data.name, data.size)}
                            />
                          </Grid>
                        </Grid>
                        {!isEmpty(fileUpload) &&
                          fileUpload.length !== index + 1 && (
                            <Divider
                              sx={{
                                margin: "0 0.5rem 0.5rem 0",
                                paddingTop: 1,
                              }}
                            />
                          )}
                      </>
                    ))}
                </Grid>
                <Grid size={{ xs: 8 }} pl={2} sx={documentMargin}>
                  {error && (
                    <Box sx={ClearErrorButtonBox}>
                      <FormHelperText error>{error}</FormHelperText>
                      <Button
                        onClick={() => setError(null)}
                        variant="contained"
                        sx={ButtonErrorWidth}
                      >
                        Clear Error
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box p={2}>
            <Typography variant="h5" sx={{ color: colors.fonts[6] }}>
              By submitting, you are attesting to the completion of the health
              plan specific NOMNC form, including member name, member insurance
              ID, last covered day and member signature (or verbal confirmation
              noted on the NOMNC).
            </Typography>
          </Box>
          <Divider sx={HorizontalDivider} />
          <Grid container sx={AddUserModalGrid}>
            <Grid size={{ xs: 12 }} sx={flexAllEnd}>
              <ButtonContained
                id="btnUploadNoMnc"
                onClick={submit}
                label="Upload NOMNC"
                disabled={false}
                style={ButtonVerifyStyle}
              />
              <ButtonContained
                id="btnCloseNomnc"
                onClick={() => setNOMNCModalflag(false)}
                label="Cancel"
                disabled={false}
                style={ButtonCloseStyle}
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default NoMncModal;
